import { AccountActivity } from '@/components/features/Account/AccountActivity';
import { AccountBreadcrumbs } from '@/components/features/Account/AccountBreadcrumbs';
import { AccountCollections } from '@/components/features/Account/AccountCollections/AccountCollections';
import { AccountContainer } from '@/components/features/Account/AccountContainer';
import { AccountGames } from '@/components/features/Account/AccountGames';
import { AccountListings } from '@/components/features/Account/AccountListings/AccountListings';
import AccountNFTList from '@/components/features/Account/AccountNFTList/AccountNFTList';
import { AccountOffers } from '@/components/features/Account/AccountOffers/AccountOffers';
import { AccountShell } from '@/components/features/Account/AccountShell';
import { allowedFilterType, formatSelectedFilters } from '@/components/features/Activity/utils';
import { useEditor } from '@/components/features/Editor/utils';
import {
  USER_TOKENS_DEFAULT_SORT_OPTION,
  USER_TOKENS_SORT_OPTIONS,
} from '@/components/features/Search/config';
import { env } from '@/env.mjs';
import { getCollections, useCollections } from '@/hooks/use-collections';
import { getUserActivity } from '@/hooks/use-user-activity';
import { getUserListings } from '@/hooks/use-user-listings';
import { getUserOffers } from '@/hooks/use-user-offers';
import { getUserTokens } from '@/hooks/use-user-tokens';
import { useAuthentication } from '@/hooks/useAuthentication';
import { getENSDetails } from '@/hooks/useENSResolver';
import { getServerAuthSession } from '@/lib/auth';
import { db } from '@/lib/db';
import { getSSRChain } from '@/lib/reservoir/get-ssr-chain';
import {
  GetUsersActivityV6Params,
  GetUsersUserTokensV7Params,
} from '@/lib/reservoir/reservoir.generated';
import { DefaultChain } from '@/utils/chains';
import { getOgImage } from '@/utils/getOgImage';
import { QueryParams } from '@/utils/getTokenQueryFromParams';
import { Container, TabList, TabPanel, TabPanels, Tabs } from '@chakra-ui/react';
import { compareAddresses, formatAddress } from '@onbeam/utils';
import { Collection, CollectionStatus, Game, User, UserRole } from '@prisma/client';
import { type useUserTokens } from '@sphere/reservoir-kit-ui';
import { TabsBarItem, TabsBarWrapper } from '@sphere/ui';
import { DehydratedState, QueryClient, dehydrate } from '@tanstack/react-query';
import { type GetServerSideProps, type NextPage } from 'next';
import { NextSeo } from 'next-seo';
import { Translate } from 'next-translate';
import useTranslation from 'next-translate/useTranslation';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { Address, checksumAddress } from 'viem';

export type UserTokens = ReturnType<typeof useUserTokens>['data'];

const tabsFn = (t: Translate) => [
  {
    title: t('tabs.nfts'),
    slug: 'nfts' as const,
    acceptedQueryParams: ['chain', 'sortBy'],
  },
  {
    title: t('tabs.games'),
    slug: 'games' as const,
    acceptedQueryParams: ['chain'],
  },
  {
    title: t('tabs.collections'),
    slug: 'collections' as const,
    acceptedQueryParams: ['chain'],
  },
  {
    title: t('tabs.listings'),
    slug: 'listings' as const,
    acceptedQueryParams: ['chain'],
  },
  {
    title: t('tabs.offers'),
    slug: 'offers' as const,
    acceptedQueryParams: ['chain'],
  },
  {
    title: t('tabs.activity'),
    slug: 'activity' as const,
    acceptedQueryParams: ['chain', 'activityFilters'],
  },
];

type TabType = ReturnType<typeof tabsFn>[number]['slug'];

const DEFAULT_TAB: TabType = 'nfts';

type Props = {
  dehydratedState: DehydratedState;
  user?: { address: string; ens?: { name?: string; avatar?: string } } & Partial<
    Pick<User, 'id' | 'role' | 'name' | 'image'>
  >;
  games?: Game[];
  notIndexedDraftCollections?: Collection[];
};

const Page: NextPage<Props> = ({ user, games, notIndexedDraftCollections }) => {
  const { t } = useTranslation('account');
  const router = useRouter();
  const { user: authenticatedUser } = useAuthentication();
  const searchParams = useSearchParams();

  useEffect(() => {
    useEditor.getState().resetState();
  }, []);

  const isOwner = compareAddresses(user?.address, authenticatedUser?.address);

  useEffect(() => {
    if (router.isReady && !router.query.chain) {
      router.replace({ query: { chain: DefaultChain.routePrefix, ...router.query } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady]);

  const tabs = tabsFn(t);
  const selectedTab = tabs.find(t => t.slug === searchParams.get('tab'))?.slug || DEFAULT_TAB;

  const setSelectedTab = (tab: TabType) => {
    router.replace({ query: { ...router.query, tab } }, undefined, { shallow: true });
  };

  const { data: collectionsData } = useCollections({
    creator: user?.address,
    ...((isOwner || authenticatedUser?.role === UserRole.ADMIN) && { excludeSpam: false }),
  });

  const collectionCount = collectionsData?.collections?.length;
  const shouldShowCollections =
    !!collectionCount ||
    (isOwner && (user?.role === UserRole.CREATOR || user?.role === UserRole.ADMIN));

  const gameCount = games?.length;
  const shouldShowGames =
    !!gameCount || (isOwner && (user?.role === UserRole.CREATOR || user?.role === UserRole.ADMIN));

  if (!user?.address) return null;

  return (
    <>
      <NextSeo
        title={user.ens?.name || formatAddress(user.address)}
        openGraph={{
          images: [
            {
              url: getOgImage({
                icon:
                  user.ens?.avatar ||
                  `${env.NEXT_PUBLIC_HOST_URL}/images/default-profile-image.png`,
                rounded: true,
                ...(user.ens?.name
                  ? { title: user.ens?.name, label: formatAddress(user.address) }
                  : { title: formatAddress(user.address) }),
              }),
              width: 1200,
              height: 630,
            },
          ],
        }}
      />
      <AccountBreadcrumbs address={user.address} displayName={user.ens?.name} />
      <AccountShell
        address={user.address}
        userId={user.id}
        role={user.role}
        displayName={user.ens?.name}
        avatar={user.ens?.avatar}
      />
      <AccountContainer>
        <Tabs isLazy index={tabs.findIndex(t => t.slug === selectedTab)}>
          <Container variant="breakout">
            <TabsBarWrapper
              mx={{ base: 'space.-24', lg: 0 }}
              stackProps={{ px: { base: 'space.24', lg: 0 }, as: TabList }}
            >
              {tabs.map(({ title, slug }) => (
                <TabsBarItem
                  id={slug}
                  key={slug}
                  onClick={() => setSelectedTab(slug)}
                  isActive={selectedTab === slug}
                  {...(slug === 'collections' && {
                    isHidden: !shouldShowCollections,
                    count: collectionCount,
                  })}
                  {...(slug === 'games' && { isHidden: !shouldShowGames, count: gameCount })}
                >
                  {title}
                </TabsBarItem>
              ))}
            </TabsBarWrapper>
          </Container>
          <TabPanels>
            <TabPanel px={0}>
              <AccountNFTList address={user.address} />
            </TabPanel>
            <TabPanel px={0}>
              {shouldShowGames && <AccountGames address={user.address} games={games} />}
            </TabPanel>
            <TabPanel px={0}>
              {shouldShowCollections && (
                <AccountCollections
                  address={user.address}
                  collections={collectionsData?.collections || []}
                  draftCollections={notIndexedDraftCollections}
                />
              )}
            </TabPanel>
            <TabPanel px={0}>
              <AccountListings address={user.address} />
            </TabPanel>
            <TabPanel px={0}>
              <AccountOffers address={user.address} />
            </TabPanel>
            <TabPanel px={0}>
              <AccountActivity address={user.address} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </AccountContainer>
    </>
  );
};

export default Page;

type Params = {
  address: string;
  chain: string;
};

export const getServerSideProps: GetServerSideProps<Props, Params> = async ctx => {
  const SSRClient = new QueryClient();
  const chain = getSSRChain(ctx);
  const session = await getServerAuthSession(ctx);

  if (!ctx.params?.address) {
    return {
      notFound: true,
    };
  }

  const address = checksumAddress(ctx.params.address as Address);

  const queryParams = ctx.query as QueryParams;
  const sortOption = queryParams.sortBy
    ? USER_TOKENS_SORT_OPTIONS.find(o => o.label === queryParams.sortBy) ||
      USER_TOKENS_DEFAULT_SORT_OPTION
    : USER_TOKENS_DEFAULT_SORT_OPTION;
  const tokenQuery: GetUsersUserTokensV7Params = sortOption.value;

  const activityTypes = (
    Array.isArray(ctx.query.activityFilters)
      ? ctx.query.activityFilters
      : [ctx.query.activityFilters || '']
  ).filter(allowedFilterType);

  const activityQuery: Partial<GetUsersActivityV6Params> = activityTypes.length
    ? { types: formatSelectedFilters(activityTypes) }
    : {};

  const isOwner = compareAddresses(address, session?.user.address);
  const isAdmin = session?.user.role === UserRole.ADMIN;

  const [user, games, collections] = await Promise.all([
    db.user.findFirst({
      where: {
        address,
      },
      select: {
        id: true,
        role: true,
        name: true,
        image: true,
      },
    }),
    db.game.findMany({
      where: {
        user: { address },
        chainId: chain.id,
        /* Only show published games to the public */
        ...(!(isOwner || isAdmin) && {
          status: CollectionStatus.PUBLISHED,
        }),
      },
    }),
    isOwner || isAdmin
      ? db.collection.findMany({
          where: {
            user: { address },
            chainId: chain.id,
          },
        })
      : undefined,
    SSRClient.prefetchQuery(
      getCollections(chain, {
        creator: address,
        /* Only hide spam collections to the public */
        ...((isOwner || isAdmin) && { excludeSpam: false }),
      }),
    ),
    SSRClient.prefetchInfiniteQuery(getUserTokens(chain, address, tokenQuery)),
    SSRClient.prefetchInfiniteQuery(getUserActivity(chain, address, activityQuery)),
    SSRClient.prefetchInfiniteQuery(getUserListings(chain, address)),
    SSRClient.prefetchInfiniteQuery(getUserOffers(chain, address)),
  ]);

  const { name, image } = await getENSDetails(address);

  /* Prevent duplicates when there is both a draft and a published version of a game. */
  const uniqueGames = [...new Map(games.map(game => [game.slug, game])).values()];

  // Only select draft collections that do not also have a published collection with that address
  const notIndexedDraftCollections = collections?.filter(
    collection =>
      collection.status !== CollectionStatus.PUBLISHED &&
      !collections.find(
        draftCollection =>
          draftCollection.address === collection.address &&
          draftCollection.status === CollectionStatus.PUBLISHED,
      ),
  );

  return {
    props: {
      dehydratedState: dehydrate(SSRClient),
      user: { ...user, address, ens: { name: name || undefined, avatar: image || undefined } },
      games: uniqueGames,
      notIndexedDraftCollections,
    },
  };
};
